import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getSession, type Agreement } from '../lib/session.ts';
import { postJson, postFormData } from '../lib/api.ts';
import EmbeddedSignature, { type EmbeddedProvider } from './EmbeddedSignature.tsx';

type SignatureMethod = EmbeddedProvider | 'govbr';

const isEmbedded = (method: SignatureMethod | null): method is EmbeddedProvider =>
  method === 'd4sign' || method === 'zapsign';

const EMBEDDED_DESCRIPTIONS: Record<EmbeddedProvider, string> = {
  d4sign: 'Sem sair desta página: você confirma com um código enviado no seu WhatsApp.',
  zapsign: 'Sem sair desta página: confira o termo, aceite e desenhe sua assinatura.',
};

const EMBEDDED_NAMES: Record<EmbeddedProvider, string> = { d4sign: 'D4Sign', zapsign: 'ZapSign' };

const optionClass = (active: boolean) =>
  `rounded-3xl border p-5 text-left transition disabled:cursor-not-allowed disabled:opacity-50 ${
    active ? 'border-red-600 bg-red-50' : 'border-slate-200 bg-white hover:bg-red-50'
  }`;

const TermPage = () => {
  const navigate = useNavigate();
  const { code } = useParams();
  const session = getSession();
  const [error, setError] = useState('');
  const [signed, setSigned] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [agreement, setAgreement] = useState<Agreement | null>(null);
  // A assinatura só abre com a entrada paga (o backend recusa antes disso).
  const [paid, setPaid] = useState<boolean | null>(null);
  const [method, setMethod] = useState<SignatureMethod | null>(null);

  useEffect(() => {
    if (!session || !session.otp || !session?.agreementCode || !code) {
      navigate('/identification');
    }
  }, [navigate, session]);

  useEffect(() => {
    if (!code) return;

    const fetchTerm = async () => {
      try {
        const a = await postJson<Agreement>(`/api/agreement/${code}/term`, {});
        setAgreement(a);
        setPaid(a.paid === true);
      } catch (error) {
        setError((error as Error).message);
      }
    };

    fetchTerm();
  }, [navigate, session?.agreementCode, code]);

  // A empresa define as formas de assinatura: a primeira já vem aberta e as demais
  // ficam atrás de "Assinar de outra forma", oferecido também quando a padrão falha.
  const providers = agreement?.signature_providers ?? [];
  const embeddedCount = providers.filter(isEmbedded).length;
  const hasAlternatives = providers.length > 1;
  const [showAlternatives, setShowAlternatives] = useState(false);

  useEffect(() => {
    const [first] = providers;
    if (first) setMethod(first);
  }, [providers.join()]);

  if (!session) return null;

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !code) return;

    setIsVerifying(true);
    setError('');
    setSigned(false);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const result = await postFormData<{ verified: boolean; error?: string }>(
        `/api/agreement/${code}/verify`, formData
      );

      if (result.verified) {
        setSigned(true);
      } else {
        setError(result.error || 'Não foi possível verificar a assinatura do arquivo enviado.');
      }
    } catch (error) {
      setError((error as Error).message || 'Erro ao verificar assinatura.');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <main className="space-y-8">
      <section className="rounded-3xl bg-white p-8 shadow-md shadow-slate-200">
        <p className="text-sm uppercase tracking-[0.3em] text-red-600">Termo de acordo</p>
        <h1 className="mt-4 text-3xl font-semibold text-red-600">Assine para concluir</h1>
        <p className="mt-3 text-slate-600">
          Entrada paga. Agora leia o termo e escolha como prefere assinar para concluir o acordo.
        </p>
      </section>

      {paid === false && (
        <section className="space-y-3 rounded-3xl bg-white p-8 shadow-sm">
          <p className="text-sm font-semibold text-slate-600">Falta o pagamento da entrada</p>
          <p className="text-sm text-slate-500">
            A assinatura do termo abre quando o banco confirmar o seu pix. Se você já pagou,
            a confirmação costuma levar alguns instantes.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/agreement/${code}/payment`)}
            className="w-full rounded-2xl bg-red-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-red-700"
          >
            Voltar para o pagamento
          </button>
        </section>
      )}

      {!isEmbedded(method) && (
        <section className="rounded-3xl bg-red-50 p-8 shadow-sm">
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-slate-500">Acordo {code}</p>
              {agreement?.term && (
                <a
                  href={agreement?.term}
                  download={`termo-acordo-${code}.pdf`}
                  className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-red-100"
                >
                  Baixar PDF
                </a>
              )}
            </div>
            {agreement?.term && (
              <iframe
                src={agreement?.term}
                title="Termo de acordo"
                className="h-[600px] w-full rounded-3xl border border-slate-200 bg-white shadow-sm"
              />
            )}
          </div>
        </section>
      )}

      {paid && hasAlternatives && showAlternatives && (
        <section className="rounded-3xl bg-white p-8 shadow-sm">
          <p className="text-sm font-semibold text-slate-600">Assinatura eletrônica</p>
          <p className="mt-1 text-sm text-slate-500">Escolha como prefere assinar o termo.</p>
          <div className={`mt-4 grid gap-3 ${providers.length > 2 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
            {providers.map((provider, index) =>
              isEmbedded(provider) ? (
                <button
                  key={provider}
                  type="button"
                  onClick={() => setMethod(provider)}
                  disabled={signed || !agreement?.term}
                  className={optionClass(method === provider)}
                >
                  <span className="block text-sm font-semibold text-slate-700">
                    {embeddedCount > 1 ? `Assinar aqui com ${EMBEDDED_NAMES[provider]}` : 'Assinar aqui agora'}
                  </span>
                  <span className="mt-1 block text-xs text-slate-500">
                    {index === 0 && 'Recomendado. '}
                    {EMBEDDED_DESCRIPTIONS[provider]}
                  </span>
                </button>
              ) : (
                <button
                  key={provider}
                  type="button"
                  onClick={() => setMethod('govbr')}
                  disabled={signed}
                  className={optionClass(method === 'govbr')}
                >
                  <span className="block text-sm font-semibold text-slate-700">Assinar com gov.br</span>
                  <span className="mt-1 block text-xs text-slate-500">
                    Baixe o PDF, assine no gov.br ou com certificado digital e envie aqui o arquivo assinado.
                  </span>
                </button>
              )
            )}
          </div>
        </section>
      )}

      {paid && isEmbedded(method) && code && (
        <EmbeddedSignature
          key={method}
          provider={method}
          code={code}
          signed={signed}
          onSigned={() => {
            setSigned(true);
            setError('');
          }}
          onFallback={hasAlternatives ? () => setShowAlternatives(true) : undefined}
        />
      )}

      {paid && method === 'govbr' && (
        <section className="rounded-3xl bg-white p-8 shadow-sm">
          <p className="text-sm font-semibold text-slate-600">Enviar termo assinado</p>
          <p className="mt-1 text-sm text-slate-500">Envie arquivo do termo assinado digitalmente pelo seu gov.br ou certificado digital.</p>
          <div className="mt-4 rounded-3xl border border-dashed border-slate-300 bg-red-50 p-6 text-center">
            <input
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              disabled={isVerifying || signed}
              className="mx-auto block w-full max-w-sm text-sm text-slate-600 file:mr-4 file:rounded-full file:border-0 file:bg-red-600 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white transition hover:file:bg-red-700 disabled:opacity-50"
            />
            {isVerifying && <p className="mt-3 text-sm text-slate-500">Verificando assinatura...</p>}
          </div>
        </section>
      )}

      {paid && hasAlternatives && !showAlternatives && !signed && (
        <button
          type="button"
          onClick={() => setShowAlternatives(true)}
          className="block w-full text-center text-sm font-semibold text-slate-500 underline transition hover:text-red-600"
        >
          Assinar de outra forma
        </button>
      )}

      {signed && <div className="rounded-2xl bg-green-50 px-4 py-3 text-sm font-semibold text-green-700">Termo assinado com sucesso.</div>}
      {error && <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <button
        onClick={() => navigate(`/agreement/${code}/success`)}
        disabled={!signed}
        className="w-full rounded-2xl bg-red-600 text-white px-5 py-4 text-sm font-semibold shadow-lg transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {signed ? 'Concluir acordo' : 'Aguardando termo assinado para prosseguir'}
      </button>
    </main>
  );
};

export default TermPage;
